import { useQuery } from '@apollo/react-hooks'
import isEmpty from 'lodash/isEmpty'
import React, { useEffect, useState, useContext, useRef } from 'react'
import { GET_CARTS } from '../graphql'
import { useUser } from '.'
import { useConfig } from '../lib'
import { useToasts } from 'react-toast-notifications'
import { isKiosk, isClient } from '../utils'
import { useTranslation } from './language'
import { indexOf, isArray, isNull } from 'lodash'
import { getAxios } from '../lib/axios'
import { isDelivery } from '../utils/isDelivery'
import { get_env } from '../utils'

export const CartContext = React.createContext()

const initialState = {
   cart: null,
   cartItems: null,
   kioskPaymentOptions: [],
}

const reducer = (state, { type, payload }) => {
   switch (type) {
      case 'CART':
         return { ...state, cart: payload }
      case 'CART_ITEMS':
         return { ...state, cartItems: payload }
      case 'KIOSK_PAYMENT_OPTION':
         return { ...state, kioskPaymentOptions: payload }
      default:
         return state
   }
}

export const CartProvider = ({ children }) => {
   const {
      brand,
      selectedOrderTab,
      locationId,
      dispatch,
      orderTabs,
      brandLocation,
      KioskConfig,
      childBrands,
   } = useConfig()
   const { addToast } = useToasts()
   const { t } = useTranslation()
   const isKioskMode = isKiosk()
   const oiType = React.useMemo(() => {
      if (isKioskMode) {
         return 'Kiosk Ordering'
      }
      return 'Website Ordering'
   }, [isKioskMode])
   const [isFinalCartLoading, setIsFinalCartLoading] = React.useState(true)

   const { isAuthenticated, user, isLoading } = useUser()
   const [cartState, cartReducer] = React.useReducer(reducer, initialState)

   const [storedCartId, setStoredCartId] = useState(null)
   const [combinedCartItems, setCombinedCartData] = useState(null)
   const [showCartIconToolTip, setShowCartIconToolTip] = useState(false)
   const [dineInTableInfo, setDineInTableInfo] = useState(null)
   const [isWalletAmountUpdating, setIsWalletAmountUpdating] = useState(false)
   const [isLoyaltyPointsUpdating, setIsLoyaltyPointsUpdating] = useState(false)
   const [isCouponUpdating, setIsCouponUpdating] = useState(false)
   const [isCampaignUpdating, setIsCampaignUpdating] = useState(false)
   const [isAddingCartItem, setIsAddingCartItem] = useState(false)
   const [isDeletingCartItem, setIsDeletingCartItem] = useState(false)
   const [
      isCartValidByProductAvailability,
      setIsCartValidByProductAvailability,
   ] = useState(false)
   const [selectedBrandIdForMenu, setSelectedBrandIdForMenu] =
      React.useState(null)
   const [cartData, setCartData] = useState({})
   const [cartItemsData, setCartItemsData] = useState([])
   const cartToolTipTimeOut = useRef()

   const showBrandSelection =
      KioskConfig?.brandSelectionPage?.show?.value && childBrands?.length > 0

   React.useEffect(() => {
      if (brand?.id && !showBrandSelection) {
         setSelectedBrandIdForMenu(brand?.id)
      }
   }, [brand?.id, showBrandSelection])

   const argsForByLocation = React.useMemo(
      () => ({
         brandId: selectedBrandIdForMenu || brand?.id,
         locationId: locationId,
         brand_locationId: brandLocation?.id,
         orderTabId: selectedOrderTab?.id,
      }),
      [brand, locationId, brandLocation?.id, selectedOrderTab]
   )

   const checkIsCartValidByProductAvailability = async (
      combinedCartItems = null
   ) => {
      if (!combinedCartItems) {
         const response = await reloadCartData()
         combinedCartItems = response.combinedCartItems
      }
      let _isCartValid = true
      if (combinedCartItems && combinedCartItems.length > 0) {
         for (let node of combinedCartItems) {
            let isCartValid = node.isAvailable

            if (!isCartValid) {
               setIsCartValidByProductAvailability(false)
               _isCartValid = false
               return
            }

            if (
               indexOf(combinedCartItems, node) ===
               combinedCartItems.length - 1
            ) {
               if (isCartValid) {
                  setIsCartValidByProductAvailability(true)
                  _isCartValid = true
               }
            }
         }
      }
      return _isCartValid
   }

   React.useEffect(() => {
      if (!combinedCartItems) return
      checkIsCartValidByProductAvailability(combinedCartItems)
   }, [combinedCartItems])

   useEffect(() => {
      if (!isEmpty(cartData) && oiType === 'Kiosk Ordering') {
         const cart = cartData
         const finalPaymentOptions = []
         cart?.paymentMethods
            .sort((a, b) => a.position - b.position)
            .forEach(option => {
               finalPaymentOptions.push({
                  identifier: option?.label,
                  label: option?.supportedPaymentOption
                     ?.paymentOptionLabelToShow,
                  id: option?.id,
               })
            })
         cartReducer({
            type: 'KIOSK_PAYMENT_OPTION',
            payload: finalPaymentOptions,
         })
      }
   }, [cartData])

   const cartToolTipStopper = () => {
      setShowCartIconToolTip(false)
   }

   const reloadCartData = async (newCartData = {}, cartId) => {
      const { cart, cartItems, combinedCartItems } = newCartData || {}
      if (cart && cartItems && combinedCartItems) {
         setCartData(cart)
         setCartItemsData(cartItems)
         setCombinedCartData(combinedCartItems)
         setIsFinalCartLoading(false)
         return true
      }

      if (!cartId) {
         if (!storedCartId) return {}
         cartId = storedCartId
      }

      // if new data is not passed then fetch the latest data from API
      const BASE_URL = get_env('BASE_BRAND_URL')
      const getCartUrl = `${BASE_URL}/server/api/cart/${cartId}`
      const { data } = await getAxios()({
         url: getCartUrl,
         method: 'GET',
      })

      if (data.success) {
         const { cart, cartItems, combinedCartItems } = data.data
         setCartData(cart)
         setCartItemsData(cartItems)
         setCombinedCartData(combinedCartItems)
         setIsFinalCartLoading(false)
         return data.data
      }

      return {}
   }

   React.useEffect(() => {
      const cartId = localStorage.getItem('cart-id')
      if (cartId) {
         setStoredCartId(+cartId)
         if (!isLoading && !isAuthenticated) {
            reloadCartData(null, +cartId)
         }
      } else {
         if (!isLoading && !isAuthenticated) {
            setStoredCartId(null)
            setIsFinalCartLoading(false)
         }
      }
   }, [isAuthenticated, isLoading])

   const _createCart = async payload => {
      try {
         const BASE_URL = get_env('BASE_BRAND_URL')
         const createCartUrl = `${BASE_URL}/server/api/cart/create`
         const { data } = await getAxios()({
            url: createCartUrl,
            method: 'POST',
            data: {
               input: payload,
            },
         })
         if (data?.success) {
            if (!isAuthenticated) {
               localStorage.setItem('cart-id', data.data.cartData.id)
            }
            setStoredCartId(data.data.cartData.id)
            setShowCartIconToolTip(true)
            if (cartToolTipTimeOut.current) {
               clearTimeout(cartToolTipTimeOut.current)
            }
            setCartItemsData(data.data.cartItems)
            setCombinedCartData(data.data.combinedCartItems)
            setCartData(data.data.cartData)
            cartToolTipTimeOut.current = setTimeout(cartToolTipStopper, 6000)
            setIsFinalCartLoading(false)
         } else {
            console.error('[Error in creating cart]: ', data.error)
            addToast(t('Failed to create cart!'), {
               appearance: 'error',
            })
         }
      } catch (error) {
         console.error('create cart error', error)
         setIsFinalCartLoading(false)
         addToast(t('Failed to create cart!'), {
            appearance: 'error',
         })
      }
   }

   const _updateCart = async ({ variables: { id, ...payload } = {} }) => {
      const BASE_URL = get_env('BASE_BRAND_URL')
      const updateCartUrl = `${BASE_URL}/server/api/cart/update`
      const updateCartResponse = await getAxios()({
         url: updateCartUrl,
         method: 'POST',
         data: {
            cartId: id,
            ...payload,
         },
      })
      if (!updateCartResponse.data.success) {
         // handle errors
         console.error(updateCartResponse.data.error)
         setIsFinalCartLoading(false)
         addToast(t('Failed to update items!'), {
            appearance: 'error',
         })
         return
      }
      addToast(t('Update Successfully!'), {
         appearance: 'success',
      })
      setIsFinalCartLoading(false)
      setCartData(updateCartResponse.data.data.cart)
      setCartItemsData(updateCartResponse.data.data.cartItems)
      setCombinedCartData(updateCartResponse.data.data.combinedCartItems)
   }

   const _deleteCart = async (cartId, updateState = true) => {
      const BASE_URL = get_env('BASE_BRAND_URL')
      const deleteCartUrl = `${BASE_URL}/server/api/cart/delete`
      const { data } = await getAxios()({
         url: deleteCartUrl,
         method: 'POST',
         data: {
            cartId,
         },
      })
      if (!data.success) {
         console.error('delete cart error')
         setIsFinalCartLoading(false)
         return
      }
      if (updateState) {
         localStorage.removeItem('cart-id')
         setStoredCartId(null)
         setIsFinalCartLoading(false)
         setCartData({})
         setCartItemsData([])
         setCombinedCartData([])
      }
   }

   const _deleteCartItems = async cartItemIds => {
      const BASE_URL = get_env('BASE_BRAND_URL')
      const deleteCartItemsUrl = `${BASE_URL}/server/api/cart-item/delete`

      const { data } = await getAxios()({
         method: 'POST',
         url: deleteCartItemsUrl,
         data: {
            cartItemIds,
            brand_locationId: brandLocation.id,
            locationId,
            orderTabId: selectedOrderTab?.id,
         },
      })

      if (!data.success) {
         // handle error
         setIsFinalCartLoading(false)
         addToast(t('Failed to delete items!'), {
            appearance: 'error',
         })
         return
      }
      addToast(t('Item removed!'), {
         appearance: 'success',
      })
      setCartItemsData(data.data.cartItems)
      setCombinedCartData(data.data.combinedCartItems)
      setCartData(data.data.cart)
      if (data.data.combinedCartItems.length === 0) {
         localStorage.removeItem('cart-id')
         setStoredCartId(null)
         setCartData({})
         setCartItemsData([])
         setCombinedCartData([])
      }
   }

   // create cartItem
   const _createCartItems = async payload => {
      const BASE_URL = get_env('BASE_BRAND_URL')
      const createCartUrl = `${BASE_URL}/server/api/cart-item/create`
      const { data } = await getAxios()({
         url: createCartUrl,
         method: 'POST',
         data: {
            input: payload,
            brand_locationId: brandLocation?.id,
         },
      })

      if (!data?.success) {
         console.log('dtata', data.error)
         return null
      }
      setCartItemsData(data.data.cartItems)
      setCombinedCartData(data.data.combinedCartItems)
      setCartData(data.data.cart)
      setIsFinalCartLoading(false)
      setShowCartIconToolTip(true)
      if (cartToolTipTimeOut.current) {
         clearTimeout(cartToolTipTimeOut.current)
      }
      cartToolTipTimeOut.current = setTimeout(cartToolTipStopper, 6000)
   }

   const _updateCartItems = async (newCartItemsPayload, oldCartItemIds) => {
      const BASE_URL = get_env('BASE_BRAND_URL')
      const updateCartItemsUrl = `${BASE_URL}/server/api/cart-item/update`
      const { data } = await getAxios()({
         method: 'POST',
         url: updateCartItemsUrl,
         data: {
            newCartItemsPayload,
            oldCartItemIds,
         },
      })

      if (!data.success) {
         addToast(t('Failed to update items!'), {
            appearance: 'error',
         })
         return null
      }
      setCartItemsData(data.data.cartItems)
      setCombinedCartData(data.data.combinedCartItems)
      setCartData(data.data.cart)
      addToast(t('Update Items successfully!'), {
         appearance: 'success',
      })
   }

   const _updateCartItemsDetails = async (
      cartItemIds,
      _set = {},
      _inc = {}
   ) => {
      const BASE_URL = get_env('BASE_BRAND_URL')
      const updateCartItemsDetailUrl = `${BASE_URL}/server/api/cart-item/details/update`
      const { data } = await getAxios()({
         method: 'POST',
         url: updateCartItemsDetailUrl,
         data: {
            cartItemIds,
            _set,
            _inc,
         },
      })

      if (!data.success) {
         addToast(t('Failed to update items detail!'), {
            appearance: 'error',
         })
         return null
      }
      setCartItemsData(data.data.cartItems)
      setCombinedCartData(data.data.combinedCartItems)
      setCartData(data.data.cart)
      addToast(t('Update Items Detail successfully!'), {
         appearance: 'success',
      })
   }

   const getCustomerAddress = () => {
      const orderTabInLocal = JSON.parse(localStorage.getItem('orderTab'))
      let customerAddressFromLocal
      switch (orderTabInLocal) {
         case 'ONDEMAND_DELIVERY':
            customerAddressFromLocal = JSON.parse(
               localStorage.getItem('userLocation')
            )
            break
         case 'PREORDER_DELIVERY':
            customerAddressFromLocal = JSON.parse(
               localStorage.getItem('userLocation')
            )
            break
         case 'PREORDER_PICKUP':
            customerAddressFromLocal = JSON.parse(
               localStorage.getItem('storeLocation')
            )
            break
         case 'ONDEMAND_PICKUP':
            customerAddressFromLocal = JSON.parse(
               localStorage.getItem('storeLocation')
            )
            break
         case 'ONDEMAND_DINEIN':
            customerAddressFromLocal = JSON.parse(
               localStorage.getItem('storeLocation')
            )
            break
         case 'SCHEDULE_DINEIN':
            customerAddressFromLocal = JSON.parse(
               localStorage.getItem('storeLocation')
            )
            break
      }

      return {
         line1: customerAddressFromLocal?.line1 || '',
         line2: customerAddressFromLocal?.line2 || '',
         city: customerAddressFromLocal?.city || '',
         state: customerAddressFromLocal?.state || '',
         country: customerAddressFromLocal?.country || '',
         zipcode: customerAddressFromLocal?.zipcode || '',
         notes: customerAddressFromLocal?.notes || '',
         label: customerAddressFromLocal?.label || '',
         lat:
            customerAddressFromLocal?.latitude?.toString() ||
            customerAddressFromLocal?.lat?.toString() ||
            '',
         lng:
            customerAddressFromLocal?.longitude?.toString() ||
            customerAddressFromLocal?.lng?.toString() ||
            '',
         landmark: customerAddressFromLocal?.landmark || '',
         searched: customerAddressFromLocal?.searched || '',
      }
   }

   const addBrandIdInCartItems = (item, brandId) => {
      item.brandId = brandId
      for (let childItem of item?.childs?.data || []) {
         addBrandIdInCartItems(childItem, brandId)
      }
   }

   //add to cart
   const addToCart = async (
      cartItem,
      quantity,
      { oldCartItemIds = null, source } = {}
   ) => {
      setIsAddingCartItem(true)
      // Add brand Id into cartItem
      let cartItems
      const brandId =
         source === 'PRODUCT_GALLERY' ? brand?.id : selectedBrandIdForMenu

      if (isArray(cartItem)) {
         for (let item of cartItem) {
            addBrandIdInCartItems(item, brandId)
         }
         cartItems = cartItem
            .map(cartItem => new Array(quantity).fill({ ...cartItem }))
            .flat()
      } else {
         addBrandIdInCartItems(cartItem, brandId)
         cartItems = new Array(quantity).fill({ ...cartItem })
      }

      const customerAddress = getCustomerAddress()

      if (!isAuthenticated) {
         //without login
         if (isEmpty(cartData)) {
            //new cart
            const object = {
               items: cartItems,
               orderInterfaceType: oiType,
               orderTabId: selectedOrderTab?.id || null,
               locationId: locationId || null,
               address: customerAddress,
               locationTableId: dineInTableInfo?.id || null,
               ...(oiType === 'Kiosk Ordering' &&
                  !isEmpty(isClient && localStorage.getItem('phone')) && {
                     customerInfo: {
                        customerPhone: localStorage.getItem('phone'),
                     },
                  }),
            }
            await _createCart(object)
         } else {
            //cart already exist
            if (isNull(oldCartItemIds)) {
               await _createCartItems({
                  cartId: cartData.id,
                  items: cartItems,
               })
            } else {
               //update cartItems
               await _updateCartItems(
                  {
                     cartId: cartData.id,
                     items: cartItems,
                  },
                  oldCartItemIds
               )
            }
         }
      } else {
         // logged in
         if (isEmpty(cartData)) {
            // new cart
            const object = {
               items: cartItems,
               orderInterfaceType: oiType,
               orderTabId: selectedOrderTab?.id || null,
               locationId: locationId || null,
               locationTableId: dineInTableInfo?.id || null,
               address: customerAddress,
            }
            await _createCart(object)
         } else {
            // update existing cart
            if (isNull(oldCartItemIds)) {
               await _createCartItems({
                  cartId: cartData.id,
                  items: cartItems,
               })
            } else {
               // update cartItems
               await _updateCartItems(
                  {
                     cartId: cartData.id,
                     items: cartItems,
                  },
                  oldCartItemIds
               )
            }
         }
      }
      setIsAddingCartItem(false)
   }

   // get user pending cart when logging in
   const { loading: existingCartLoading, error: existingCartError } = useQuery(
      GET_CARTS,
      {
         variables: {
            where: {
               paymentStatus: { _eq: 'PENDING' },
               status: { _eq: 'CART_PENDING' },
               customerKeycloakId: {
                  _eq: user?.keycloakId,
               },
               source: {
                  _neq: 'subscription',
               },
               brandId: {
                  _eq: brand?.id,
               },
               isArchived: {
                  _eq: false,
               },
               usedOrderInterface: {
                  _neq: 'App Ordering',
               },
            },
         },
         skip: !(brand?.id && user?.keycloakId && orderTabs.length > 0),
         fetchPolicy: 'no-cache',
         onCompleted: async data => {
            if (!data) {
               return
            }
            const { carts } = data

            // pending cart available
            if (carts && carts.length > 0) {
               const guestCartId = localStorage.getItem('cart-id')
               if (guestCartId) {
                  // delete pending cart and assign guest cart to the user
                  await _updateCart({
                     variables: {
                        id: guestCartId,
                        _set: {
                           paymentMethodId:
                              user.platform_customer?.defaultPaymentMethodId,
                           ...(user.brandCustomers?.firstName && {
                              customerInfo: {
                                 customerFirstName:
                                    user.brandCustomers?.firstName,
                                 customerLastName:
                                    user.brandCustomers?.lastName,
                                 customerEmail: user.brandCustomers?.email,
                                 customerPhone:
                                    user.brandCustomers?.phoneNumber,
                              },
                           }),
                        },
                     },
                  })
                  // delete last one
                  await _deleteCart(carts[0]?.id, false)
                  localStorage.removeItem('cart-id')
                  setStoredCartId(guestCartId)
                  setIsFinalCartLoading(false)
               } else {
                  const addressInCart = carts[0].address
                  const addressToBeSaveInLocal = {
                     city: addressInCart?.city,
                     country: addressInCart?.country,
                     label: addressInCart?.label,
                     landmark: addressInCart?.landmark,
                     latitude: addressInCart?.lat,
                     line1: addressInCart?.line1,
                     line2: addressInCart?.line2,
                     longitude: addressInCart?.lng,
                     mainText: addressInCart?.line1,
                     notes: addressInCart?.notes,
                     secondaryText: `${addressInCart?.city}, ${addressInCart?.state} ${addressInCart?.zipcode}, ${addressInCart?.country}`,
                     state: addressInCart?.state,
                     zipcode: addressInCart?.zipcode,
                     searched: addressInCart?.searched || '',
                  }
                  const orderTabForLocal =
                     carts[0].fulfillmentInfo?.type ||
                     orderTabs.find(
                        eachOrderTab => eachOrderTab.id === carts[0].orderTabId
                     )?.orderFulfillmentTypeLabel
                  const locationIdForLocal = carts[0].locationId
                  localStorage.setItem(
                     'orderTab',
                     JSON.stringify(orderTabForLocal)
                  )
                  if (
                     orderTabForLocal === 'ONDEMAND_PICKUP' ||
                     orderTabForLocal === 'PREORDER_PICKUP'
                  ) {
                     localStorage.setItem(
                        'storeLocation',
                        JSON.stringify(addressToBeSaveInLocal)
                     )
                  } else if (
                     orderTabForLocal === 'PREORDER_DELIVERY' ||
                     orderTabForLocal === 'ONDEMAND_DELIVERY'
                  ) {
                     localStorage.setItem(
                        'userLocation',
                        JSON.stringify(addressToBeSaveInLocal)
                     )
                     dispatch({
                        type: 'SET_USER_LOCATION',
                        payload: addressToBeSaveInLocal,
                     })
                  }
                  localStorage.setItem(
                     'storeLocationId',
                     JSON.stringify(locationIdForLocal)
                  )
                  dispatch({
                     type: 'SET_LOCATION_ID',
                     payload: locationIdForLocal,
                  })
                  dispatch({
                     type: 'SET_SELECTED_ORDER_TAB',
                     payload: orderTabs.find(
                        eachOrderTab => eachOrderTab.id === carts[0].orderTabId
                     ),
                  })
                  dispatch({
                     type: 'SET_STORE_STATUS',
                     payload: {
                        status: true,
                        message: 'Store available on your location.',
                        loading: false,
                     },
                  })
                  setStoredCartId(carts[0].id)
                  await reloadCartData(null, carts[0].id)
                  localStorage.removeItem('cart-id')
                  setIsFinalCartLoading(false)
               }
            } else {
               // no pending cart
               if (storedCartId) {
                  await _updateCart({
                     variables: {
                        id: storedCartId,
                        _set: {
                           paymentMethodId:
                              user.platform_customer?.defaultPaymentMethodId,
                           ...(user.brandCustomers?.firstName && {
                              customerInfo: {
                                 customerFirstName:
                                    user.brandCustomers?.firstName,
                                 customerLastName:
                                    user.brandCustomers?.lastName,
                                 customerEmail: user.brandCustomers?.email,
                                 customerPhone:
                                    user.brandCustomers?.phoneNumber,
                              },
                           }),
                        },
                     },
                  })
                  localStorage.removeItem('cart-id')
                  setIsFinalCartLoading(false)
               } else {
                  setCombinedCartData([])
                  setIsFinalCartLoading(false)
               }
            }
         },
      }
   )

   const reorder = async reOrderCartId => {
      setIsFinalCartLoading(true)
      const BASE_URL = get_env('BASE_BRAND_URL')

      try {
         const orderTabInLocal = localStorage.getItem('orderTab')

         let address = isDelivery(JSON.parse(orderTabInLocal))
            ? localStorage.getItem('userLocation')
            : localStorage.getItem('storeLocation')
         address = JSON.parse(address)
         if (address) {
            address.lng = address?.longitude || ''
            address.lat = address?.latitude || ''
            if (address?.longitude) delete address.longitude
            if (address?.latitude) delete address.latitude
            address = JSON.stringify(address)
         }

         const { data, status } = await getAxios()({
            method: 'POST',
            url:
               `${BASE_URL}/server/api/re-order?reOrderCartId=${reOrderCartId}&keycloakId=${user.keycloakId}&brandId=${brand.id}&usedOrderInterface=Website Ordering` +
               (storedCartId ? `&currentCartId=${storedCartId}` : '') +
               (address ? `&address=${encodeURIComponent(address)}` : '') +
               (locationId ? `&locationId=${locationId}` : '') +
               (selectedOrderTab?.id
                  ? `&orderTabId=${selectedOrderTab.id}`
                  : ''),
         })
         if (status == 200) {
            if (!storedCartId) {
               setStoredCartId(data.cartId)
            }
            addToast(t('Items added successfully'), {
               appearance: 'success',
            })
         } else {
            console.error('something went wrong in re-order', data)
            addToast(t('Something went wrong'), {
               appearance: 'error',
            })
         }
      } catch (error) {
         console.error('something went wrong in re-order', error)
         addToast(t('Something went wrong'))
      }
   }

   return (
      <CartContext.Provider
         value={{
            cartState: {
               cart: !isEmpty(cartData) ? cartData : {} || {},
               cartItems: cartItemsData || [],
               kioskPaymentOptions: cartState.kioskPaymentOptions,
            },
            reloadCartData,
            isFinalCartLoading,
            cartReducer,
            addToCart,
            combinedCartItems,
            setStoredCartId,
            storedCartId,
            showCartIconToolTip,
            setDineInTableInfo,
            dineInTableInfo,
            methods: {
               cartItems: {
                  delete: async (...args) => {
                     setIsDeletingCartItem(true)
                     await _deleteCartItems(...args)
                     setIsDeletingCartItem(false)
                  },
                  update: _updateCartItemsDetails,
               },
               cart: {
                  update: _updateCart,
                  delete: _deleteCart,
               },
            },
            isCartValidByProductAvailability,
            checkIsCartValidByProductAvailability,
            reorder,
            oiType,
            getCustomerAddress,
            isCouponUpdating,
            setIsCouponUpdating,
            isCampaignUpdating,
            setIsCampaignUpdating,
            isDeletingCartItem,
            isAddingCartItem,
            isWalletAmountUpdating,
            isLoyaltyPointsUpdating,
            setIsWalletAmountUpdating,
            setIsLoyaltyPointsUpdating,
            selectedBrandIdForMenu,
            setSelectedBrandIdForMenu,
         }}
      >
         {children}
      </CartContext.Provider>
   )
}

export const useCart = () => useContext(CartContext)
